import React, { useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

interface OrdersProps {
  onNavigate: (page: string) => void;
}

const Orders: React.FC<OrdersProps> = ({ onNavigate }) => {
  const { orders, loadOrders } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadOrders(user.id);
    }
  }, [user, loadOrders]);

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📦</div>
          <h2 className="text-3xl font-black text-slate-900 mb-4">No orders yet</h2>
          <p className="text-slate-600 mb-8">Start shopping to see your orders here</p>
          <button
            onClick={() => onNavigate('home')}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-black text-slate-900 mb-8">My Orders</h1>

        <div className="space-y-6">
          {orders.map((order) => (
            <div 
              key={order.id} 
              onClick={() => onNavigate('order-detail-' + order.id)}
              className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-blue-200 border border-transparent transition-all group"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">Order #{order.id}</h3>
                  <p className="text-slate-600">{new Date(order.date).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-col items-end space-y-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    order.status === 'ORDERED' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'DELIVERING' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                    }`}>
                    {order.status}
                    </span>
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">View Invoice →</span>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-center py-2">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div className="flex-1 ml-4">
                      <div className="font-semibold text-slate-900">{item.name}</div>
                      <div className="text-slate-600 text-sm">Qty: {item.quantity} × ${item.price.toFixed(2)}</div>
                    </div>
                    <div className="font-bold text-slate-900">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-slate-500">Total:</span>
                  <span className="text-blue-600">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Orders;
